import { useEffect } from "react";

import {
    WithAnalyticsEventsProps,
    withAnalyticsEvents
} from "@atlaskit/analytics-next";

import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { useOneTrustContext } from "../../utils/onetrust-provider";
import { useAnalyticsClient } from "../wac-analytics/analytics-client";

import { AnalyticsData } from "./gasv3";
import { Initializer } from "./initializer";

export type InitializeAutoAtlAnalyticsProps = {
    readonly pageViewProperties?: AnalyticsData;
} & WithAnalyticsEventsProps;

const deepClone: <T extends object>(obj: T) => T = (obj) => {
    return JSON.parse(JSON.stringify(obj)) as typeof obj;
};

const InitializeAutoAtlAnalyticsImpl: React.FC<
    InitializeAutoAtlAnalyticsProps
> = ({
    pageViewProperties = {},
    createAnalyticsEvent
}: InitializeAutoAtlAnalyticsProps) => {
    const analyticsClient = useAnalyticsClient();
    const { analyticsOptOut, isConsentLoaded } = useOneTrustContext();
    const ANALYTICS_WRITE_KEY = process.env["ANALYTICS_SOURCE_ID"] || "";

    const isEnableOperationalEventWhenOptedOut = checkFeatureFlagGate(
        featureFlags.gwp10416DisableNonOperationalAnalyticsGateUip
    );

    useEffect(() => {
        if (isEnableOperationalEventWhenOptedOut) {
            if (!analyticsClient || !isConsentLoaded) {
                return;
            }
        } else {
            if (!analyticsClient || analyticsOptOut || !isConsentLoaded) {
                return;
            }
        }

        Initializer.initWithPageAnalytics(
            ANALYTICS_WRITE_KEY,
            deepClone(pageViewProperties),
            {},
            Date.now(),
            analyticsClient.getAnonymousId() ?? "",
            createAnalyticsEvent || null,
            analyticsOptOut ?? true
        );
    }, [
        analyticsClient,
        analyticsOptOut,
        isConsentLoaded,
        ANALYTICS_WRITE_KEY,
        createAnalyticsEvent,
        pageViewProperties,
        isEnableOperationalEventWhenOptedOut
    ]);

    return null;
};

export const InitializeAutoAtlAnalytics = withAnalyticsEvents()(
    InitializeAutoAtlAnalyticsImpl
);
